// Pure function: (nodes, edges) -> nodes with dagre-computed positions.
//
// Used by CanvasWorkflowPanel's "Auto-layout" header button so authors can
// reset the graph to a deterministic top-down layout after manual drags
// drift it out of shape. React Flow's drag state lives in component memory
// only — there's no persistence layer yet — so this is the "fix the mess"
// affordance until the canvasWorkflowLayout slice lands.
//
// Pure: takes React Flow nodes + edges, returns a new nodes array. Existing
// edge objects are not touched. Node identity (id, type, data, selected, …)
// is preserved; only `position` is replaced with the dagre-computed coords,
// translated from dagre's center-anchored output to React Flow's
// top-left-anchored input.

import dagre from "dagre";

// Match the card dimensions buildElementGraph uses so the auto-layout
// matches the post-build visual density. Slightly looser ranksep/nodesep
// than the dagre defaults so 3+ outgoing edges per node don't pile up.
const NODE_W = 220;
const NODE_H = 110;
const RANKSEP = 80;
const NODESEP = 40;

/**
 * Run dagre on a React Flow nodes+edges pair and return the nodes with
 * dagre-computed positions. Pure — no React, no Redux, no side effects.
 *
 * @param {Array<{id: string, position: {x:number,y:number}, ...}>} nodes
 * @param {Array<{source: string, target: string, ...}>} edges
 * @returns {Array<{...node, position: {x:number,y:number}}>} new nodes array
 */
export function applyDagreLayout(nodes, edges) {
  if (!Array.isArray(nodes) || nodes.length === 0) return nodes || [];
  const safeEdges = Array.isArray(edges) ? edges : [];

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: RANKSEP, nodesep: NODESEP });
  g.setDefaultEdgeLabel(() => ({}));

  const validIds = new Set(nodes.map((n) => n.id));
  for (const n of nodes) {
    g.setNode(n.id, { width: NODE_W, height: NODE_H });
  }
  for (const e of safeEdges) {
    if (!e || !validIds.has(e.source) || !validIds.has(e.target)) continue;
    g.setEdge(e.source, e.target);
  }

  dagre.layout(g);

  return nodes.map((n) => {
    const laid = g.node(n.id);
    if (!laid) return n;
    // dagre returns center coords; React Flow positions by top-left corner.
    return {
      ...n,
      position: {
        x: laid.x - NODE_W / 2,
        y: laid.y - NODE_H / 2,
      },
    };
  });
}
